import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import { Tender } from '../../models';
import { Offer } from '../../models/offer';
import { TenderContractsAwardedService } from '../../services';

@Component({
    selector: 'jft-tender-contracts-awarded',
    templateUrl: './tender-contracts-awarded.component.html',
    styleUrls: ['./tender-contracts-awarded.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderContractsAwardedComponent implements OnInit, OnDestroy {

    public loading: boolean = false;
    public tender: Tender;
    public offers: Offer[] = [];

    public orderField: string;
    public order: string;

    private termSubscription: Subscription;
    private updateContractsSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private service: TenderContractsAwardedService,
        private loadingIndicator: LoadingIndicatorService
    ) {
    }

    public ngOnInit() {
        this.dataInit();
        this.orderInit();
        this.searchInit();
    }

    private dataInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.parent.snapshot.data;

        // Don't load offers from resolver - let parent component's loadLocationTags() trigger it
        // This ensures saved tag filters are loaded before fetching data
        this.tender = parentData['tender'];

        // Subscribe to updates from parent component's filter changes
        this.updateContractsSubscription = this.service.updateContractsObservable.subscribe(item => {
            this.updateOffers();
        });
    }

    private updateOffers() {
        this.loading = true;
        this.loadingIndicator.show();

        this.service.fetch(this.tender.id).subscribe((offers) => {
            this.offers = offers;
            this.loading = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    private searchInit() {
        this.service.setFilter(null);

        this.termSubscription = this.service.searchFieldObservable
            .pipe(debounceTime(500))
            .pipe(distinctUntilChanged())
            .subscribe((term: string) => {
                this.loading = true;
                this.loadingIndicator.show();

                this.service.setFilter(term);
                this.service.fetch(this.tender.id).subscribe((contracts) => {
                    this.offers = contracts;
                    this.loading = false;
                    this.loadingIndicator.hide();
                }, (error) => {
                    this.loading = false;
                    this.loadingIndicator.hide();
                    this.toast.error(error, this.tender.name);
                });
            });
    }

    private orderInit() {
        let order = this.service.getOrder();
        if (order) {
            this.order = order.startsWith('-') ? 'DESC' : 'ASC';
            this.orderField = order.startsWith('-') ? order.slice(1) : order;
        }
    }

    public sort($event: Event, field: string) {
        this.loading = true;
        this.loadingIndicator.show();

        if (this.orderField == field) {
            this.order = this.order == 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.order = 'ASC';
        }
        this.orderField = field;

        this.service.setOrder(this.orderField, this.order).fetch(this.tender.id)
            .subscribe((offers) => {
                this.loading = false;
                this.loadingIndicator.hide();
                this.offers = offers;
        }, (e) => {
                this.loading = false;
                this.loadingIndicator.hide();
                this.toast.error(e, this.tender.name);
        });
    }

    public isDateExpired(date) {
        return DateTimeHelper.isDateExpired(date)
    }

    public ngOnDestroy(): void {
        this.termSubscription.unsubscribe();
        this.updateContractsSubscription.unsubscribe();
    }
}
